import { apiErrorMessage } from '@agos/api-client'
import {
  getGetOrdersQueryKey,
  useGetOrders,
  useGetSalesChannels,
  usePatchOrdersOrderId,
} from '@agos/api-client/orders'
import type { OrderListItem } from '@agos/api-client/model'
import { ARCHIVE_ORDER_PARAM, channelName, EmployeeAuthModal, filterChannelsOf, formatCents, formatMoney, parseMoneyToCents, sumCents, T, tt, type AuthActionMeta, type FilterChannel } from '@agos/shared'
import {
  BUTTON_GHOST,
  cn,
  EmptyState,
  PageHeader,
  TabGroup,
  toast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  RefreshCw,
  Filter,
  Search,
  Store,
  ShoppingBag,
  Truck,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'wouter'

import {
  ORDER_STATUS_LABELS,
  orderStatusLabel,
  paymentMethodLabel,
} from '../order-status'
import { EditedFlag, OrderRow, SourceBadge, StatusPill } from './OrderRow'
import { PROTECTED_STATUSES } from './status-transitions'

/**
 * `/lists` — *Zoznamy & Archív*, ported from legacy `pages/lists.tsx`.
 *
 * The archive is the order history, and the only screen from which a finished
 * order can still be changed. Everything that can change one is gated: a
 * protected status and a re-open both ask for credentials, and the two admin
 * overrides ask for a password before applying an ordinary PATCH.
 *
 * There is no poll here. Unlike the kitchen and the dispatch boards (§14.1),
 * this page looks backwards, and the 15-second `staleTime` is what legacy sets.
 */

const PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300
const ARCHIVE_STALE_MS = 15_000

const TYPE_ICONS: Record<string, typeof Store> = {
  delivery: Truck,
  restaurant: Store,
  bar: ShoppingBag,
}

const FILTER_CLASSES =
  'px-3 py-2.5 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

type ArchiveTab = 'archive' | 'export'

export function ListsPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [tab, setTab] = useState<ArchiveTab>('archive')
  const [pendingAction, setPendingAction] = useState<AuthActionMeta | null>(null)
  const [page, setPage] = useState(1)
  /**
   * Seeded from the URL — §9.211.
   *
   * *Štatistiky* links here with `?discounted=true&from=…&to=…`, and a link that carried a period
   * the page then ignored would show the whole archive under a heading naming one month. Read once,
   * because after that they are the operator's to change; `useSearchParams` is declared below and a
   * lazy initialiser reads the same query string it does.
   */
  const [dateFrom, setDateFrom] = useState(
    () => new URLSearchParams(window.location.search).get('from') ?? '',
  )
  const [dateTo, setDateTo] = useState(
    () => new URLSearchParams(window.location.search).get('to') ?? '',
  )

  /**
   * §9.143 — `/lists?order=<id>`, which is where every order-number link in the manager lands.
   *
   * Read from the URL rather than held in state, so that the back button and a pasted link behave the
   * same way, and so that clearing it is a navigation the browser knows about.
   */
  const [params, setParams] = useSearchParams()
  const linkedOrderId = params.get(ARCHIVE_ORDER_PARAM)
  /**
   * §9.211 — only the orders that carried a discount.
   *
   * In the URL rather than in local state: *"show me August's discounts"* is a thing somebody sends,
   * and it is what the link from *Štatistiky* is. Local state would make that link impossible and
   * would forget the filter on a reload.
   */
  const discountedOnly = params.get('discounted') === 'true'
  const clearLinkedOrder = () => {
    const next = new URLSearchParams(params)
    next.delete(ARCHIVE_ORDER_PARAM)
    setParams(next, { replace: true })
  }

  // The franchise's own channels. A fourth one appears here the day it is switched on, and its name is
  // the one the franchise chose — FB-174's *Osobný odber* versus BAR.
  const { data: salesChannels } = useGetSalesChannels()
  const channels = useMemo(() => filterChannelsOf(salesChannels), [salesChannels])

  const queryClient = useQueryClient()
  const patchOrder = usePatchOrdersOrderId()

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const { data: result, isLoading } = useGetOrders(
    {
      status: filterStatus || undefined,
      type: filterType || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      // Without a date range the archive means the whole history, and the
      // endpoint defaults to today.
      all_time: !dateFrom && !dateTo ? 'true' : undefined,
      search: debouncedSearch || undefined,
      // §10A.73: exact, and on its own — the linked order is usually outside whatever window the
      // other filters describe, and the server answers it by id alone.
      order_id: linkedOrderId ?? undefined,
      // §10A.86 — travels with the date range rather than replacing it, which is the opposite of
      // `order_id`: a linked order escapes the window, a period's discounts are the window.
      discounted: discountedOnly ? 'true' : undefined,
      page: String(page),
      page_size: String(PAGE_SIZE),
    },
    { query: { staleTime: ARCHIVE_STALE_MS } },
  )

  const orders = result?.data ?? []
  const serverTotal = result?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(serverTotal / PAGE_SIZE))

  /**
   * §9.147 / §10A.74 — counted by the server over the whole filter, not by the browser over the page
   * it happens to be holding.
   *
   * Measured on 28 July 2026: these read *Doručených 43* beside *Celkovo 74* on a day with 67
   * delivered orders, because 43 is how many of the fifty rows on page one were delivered. Four
   * cards in a row, two populations, and paging moved three of them.
   */
  const statusCounts = result?.statusCounts ?? {}
  const countOf = (status: string) => Number(statusCounts[status] ?? 0)
  const delivered = countOf('delivered')
  const cancelled = countOf('cancelled')
  const qualityCheck = countOf('quality_check')

  /**
   * The server sends the takings for the whole filtered set; the client sum is
   * only a fallback and can only see this page. §14.7: it is assembled in cents,
   * because legacy adds `numeric` strings with `+` and gets `"012.50"`.
   */
  const revenueCents =
    result?.totalRevenue === undefined
      ? sumCents(
          ...orders.filter((order) => order.status !== 'cancelled').map((order) => order.total),
        )
      : parseMoneyToCents(result.totalRevenue)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() })
  }

  const resetPage = () => setPage(1)

  const metaFor = (order: OrderListItem): Pick<
    AuthActionMeta,
    'orderId' | 'orderNumber' | 'orderTotalCents'
  > => ({
    orderId: order.id ?? 0,
    orderNumber: order.orderNumber ?? '',
    orderTotalCents: parseMoneyToCents(order.total),
  })

  /** A payment change always asks, whoever is asking. It is a money field. */
  const handlePaymentMethodRequest = (order: OrderListItem, newMethod: string) => {
    setPendingAction({
      ...metaFor(order),
      newPaymentMethod: newMethod,
      previousPaymentMethod: order.paymentMethod,
      customTitle: `${tt('Zmena platby')}: ${paymentMethodLabel(order.paymentMethod)} → ${paymentMethodLabel(newMethod)}`,
      noReason: true,
    })
  }

  const handleStatusRequest = (order: OrderListItem, newStatus: string, forceAuth?: boolean) => {
    if (PROTECTED_STATUSES.includes(newStatus)) {
      setPendingAction({ ...metaFor(order), newStatus })
      return
    }
    if (forceAuth) {
      // The admin override. The status is not protected, so the server will not
      // ask — the screen asks instead, then PATCHes.
      setPendingAction({ ...metaFor(order), newStatus, useDirectPatch: true })
      return
    }
    patchOrder.mutate(
      { orderId: order.id ?? 0, data: { status: newStatus } },
      {
        onSuccess: () => {
          refresh()
          toast({ title: tt('Stav zmenený'), description: `→ ${orderStatusLabel(newStatus)}` })
        },
        // §9.385 - the status guards refuse by name (a printed receipt, a settled order), and
        // *Chyba* was the screen replacing all of it.
        onError: (caught) =>
          toast({
            variant: 'destructive',
            title: tt('Chyba'),
            description: apiErrorMessage(caught, tt('Nepodarilo sa zmeniť stav.')),
          }),
      },
    )
  }

  const handleReturnToKitchen = (order: OrderListItem) => {
    setPendingAction({ ...metaFor(order), returnToKitchen: true })
  }

  const handleAuthSuccess = (confirmedByName: string) => {
    if (!pendingAction) return
    const action = pendingAction
    setPendingAction(null)

    if (action.returnToKitchen) {
      refresh()
      toast({
        title: tt('Vrátené do kuchyne'),
        description: `${tt('Objednávka')} #${action.orderNumber} · ${tt('overil')}: ${confirmedByName}`,
      })
      return
    }

    if (action.newPaymentMethod) {
      patchOrder.mutate(
        { orderId: action.orderId, data: { paymentMethod: action.newPaymentMethod } },
        {
          onSuccess: () => {
            refresh()
            toast({
              title: `${tt('Spôsob platby zmenený')}: ${paymentMethodLabel(action.previousPaymentMethod)} → ${paymentMethodLabel(action.newPaymentMethod)}`,
              description: `${tt('Overil')}: ${confirmedByName}`,
            })
          },
          // §9.385 - `lockedPaymentMethod` refuses this once a fiscal document exists, and says so.
          onError: (caught) =>
            toast({
              variant: 'destructive',
              title: tt('Chyba'),
              description: apiErrorMessage(caught, tt('Nepodarilo sa zmeniť spôsob platby.')),
            }),
        },
      )
      return
    }

    if (action.useDirectPatch) {
      patchOrder.mutate(
        { orderId: action.orderId, data: { status: action.newStatus } },
        {
          onSuccess: () => {
            refresh()
            toast({
              title: `${tt('Stav zmenený')}: ${orderStatusLabel(action.newStatus)}`,
              description: `${tt('Overil')}: ${confirmedByName}`,
            })
          },
          // §9.385 - as above; this is the row-level control rather than the bulk one.
          onError: (caught) =>
            toast({
              variant: 'destructive',
              title: tt('Chyba'),
              description: apiErrorMessage(caught, tt('Nepodarilo sa zmeniť stav.')),
            }),
        },
      )
      return
    }

    // The modal already posted to confirm-status; there is nothing left to send.
    refresh()
    toast({
      title: `${tt('Stav zmenený')}: ${orderStatusLabel(action.newStatus)}`,
      description: `${tt('Overil')}: ${confirmedByName}`,
    })
  }

  const handleStornoSuccess = (response: Record<string, unknown> | undefined) => {
    const message = typeof response?.message === 'string' ? response.message : undefined
    if (response?.virtual === true) {
      // §11.4: a virtual receipt never reached a register, so the storno is a
      // local mark. Saying "stornované" without saying that would be a lie the
      // manager only discovers at the tax audit.
      toast({
        title: tt('Storno označené lokálne'),
        description:
          message ?? tt('Doklad bol vystavený vo virtuálnom režime - kasa o ňom nevie.'),
        duration: 8000,
      })
      return
    }
    toast({
      title: tt('Storno vykonané'),
      description: message ?? tt('Fiškálny doklad bol stornovaný.'),
    })
  }

  const rowHandlers = {
    onStatusRequest: handleStatusRequest,
    onPaymentMethodRequest: handlePaymentMethodRequest,
    onReturnToKitchen: handleReturnToKitchen,
    onStornoSuccess: handleStornoSuccess,
    onRefresh: refresh,
  }

  return (
    <div className="space-y-5">
      <PageHeader title={tt('Zoznamy & Archív')} />

      {/* §9.270 - `lg:` rather than `md:`. `md:` is a 768 px *viewport* step and the sidebar takes
        * 256 px of it, so a tablet at 820 split 516 px five ways — about 90 px a tile — and a grid
        * item's default `min-width: auto` will not let a money figure shrink into that. Measured: the
        * row was 29 px wider than its container and the document scrolled sideways with it. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <SummaryCard
          label={tt('Celkovo')}
          value={String(serverTotal)}
          tone="text-foreground"
          note={tt('vrátane zrušených')}
        />
        <SummaryCard label={tt('Doručených')} value={String(delivered)} tone="text-emerald-500" />
        <SummaryCard label={tt('Zrušených')} value={String(cancelled)} tone="text-red-500" />
        <SummaryCard
          label={tt('Kontr. kvality')}
          value={String(qualityCheck)}
          tone="text-cyan-500"
        />
        {/* §9.147 - the same day is worth a different amount on three screens, and none of them
          * used to say why. This one leaves out the cancelled orders. */}
        <SummaryCard
          label={tt('Celkový obrat')}
          value={formatCents(revenueCents)}
          tone="text-primary"
          note={tt('bez zrušených')}
        />
      </div>

      <TabGroup
        tabs={[
          { id: 'archive', label: tt('Archív objednávok') },
          { id: 'export', label: tt('Export & filtrovanie') },
        ]}
        active={tab}
        onChange={(id) => setTab(id as ArchiveTab)}
      />

      {/* §9.143 - a single row out of ten thousand reads as a broken filter unless the screen says
        * why it is showing one. Named, and clearable in one click. */}
      {linkedOrderId !== null && (
        <div
          data-linked-order={linkedOrderId}
          className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-primary/30 bg-primary/5 text-sm"
        >
          <Filter className="w-4 h-4 text-primary shrink-0" />
          <span className="text-foreground">
            <T>Zobrazená je jedna objednávka</T>
            {': '}
            <span className="font-mono font-bold">#{linkedOrderId}</span>
          </span>
          <button
            type="button"
            onClick={clearLinkedOrder}
            className="ml-auto px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50 transition-colors"
          >
            <T>Zobraziť všetky</T>
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={tt('Hľadať podľa čísla, mena, tel., adresy...')}
            aria-label={tt('Hľadať podľa čísla, mena, tel., adresy...')}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(event) => {
            setFilterStatus(event.target.value)
            resetPage()
          }}
          aria-label={tt('Všetky stavy')}
          className={FILTER_CLASSES}
        >
          <option value="">{tt('Všetky stavy')}</option>
          {Object.entries(ORDER_STATUS_LABELS).map(([status, label]) => (
            <option key={status} value={status}>
              {label}
            </option>
          ))}
        </select>
        {/* §9.5: the channels come from the server, not from a hard-coded map. `ORDER_TYPE_LABELS`'s own
          * docblock asked for this after parity, and a map cannot name a channel it has never heard of. */}
        <select
          value={filterType}
          onChange={(event) => {
            setFilterType(event.target.value)
            resetPage()
          }}
          aria-label={tt('Všetky typy')}
          data-channel-filter
          className={FILTER_CLASSES}
        >
          <option value="">{tt('Všetky typy')}</option>
          {channels.map((channel) => (
            <option key={channel.slug} value={channel.slug}>
              {channel.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(event) => {
            setDateFrom(event.target.value)
            resetPage()
          }}
          className={FILTER_CLASSES}
          title={tt('Od dátumu')}
          aria-label={tt('Od dátumu')}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(event) => {
            setDateTo(event.target.value)
            resetPage()
          }}
          className={FILTER_CLASSES}
          title={tt('Do dátumu')}
          aria-label={tt('Do dátumu')}
        />
        {(dateFrom || dateTo) && (
          <button
            type="button"
            onClick={() => {
              setDateFrom('')
              setDateTo('')
              resetPage()
            }}
            className="px-3 py-2.5 rounded-xl bg-red-500/10 text-red-500 text-sm hover:bg-red-500/20 transition-colors"
          >
            <T>Zrušiť dátumy</T>
          </button>
        )}
        {/* §9.211 - the archive already filters by date and already draws each discount's source,
          * so this one control turns it into the report the owner asked for. */}
        <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-muted text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={discountedOnly}
            aria-label={tt('Iba so zľavou')}
            data-discounted-filter={discountedOnly ? 'on' : 'off'}
            onChange={(event) => {
              const next = new URLSearchParams(params)
              if (event.target.checked) next.set('discounted', 'true')
              else next.delete('discounted')
              setParams(next, { replace: true })
              resetPage()
            }}
            className="w-4 h-4 rounded accent-primary"
          />
          <T>Iba so zľavou</T>
        </label>
        <button
          type="button"
          onClick={refresh}
          aria-label={tt('Obnoviť')}
          className={cn(BUTTON_GHOST, 'flex items-center gap-2 px-3 py-2.5 rounded-xl bg-muted text-sm')}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        {tab === 'export' && (
          <button
            type="button"
            disabled
            title={tt('Export zatiaľ nie je dostupný')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-muted-foreground text-sm font-semibold transition-all ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" /> <T>Export CSV</T>
          </button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {orders.length}{' '}
        {orders.length === 1 ? tt('objednávka') : tt('objednávok')}
        {orders.length !== serverTotal && ` ${tt('z')} ${serverTotal} ${tt('celkovo')}`}
        {tab === 'archive' && (
          <span className="ml-2 text-primary">
            <T>· Klikni na riadok pre detail a zmenu stavu</T>
          </span>
        )}
      </p>

      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        {isLoading ? (
          <div className="p-10 text-center text-muted-foreground text-sm">
            <T>Načítavam...</T>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-12">
            <EmptyState
              icon={<FileText className="w-10 h-10" />}
              title={tt('Žiadne objednávky')}
              description={tt('Skúste zmeniť filtre')}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/30">
                <tr className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  <th className="text-left px-4 py-3">
                    <T>Číslo</T>
                  </th>
                  <th className="text-left px-4 py-3">
                    <T>Typ</T>
                  </th>
                  <th className="text-left px-4 py-3">
                    <T>Zákazník</T>
                  </th>
                  <th className="text-left px-4 py-3">
                    <T>Položky</T>
                  </th>
                  <th className="text-right px-4 py-3">
                    <T>Suma</T>
                  </th>
                  <th className="text-left px-4 py-3">
                    <T>Stav</T>
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) =>
                  tab === 'archive' ? (
                    <OrderRow key={order.id} order={order} {...rowHandlers} />
                  ) : (
                    <ExportRow key={order.id} order={order} channels={channels} />
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}

        {orders.length > 0 && (
          <div className="px-4 py-3 border-t border-border bg-muted/20 flex items-center justify-between gap-4 flex-wrap">
            <span className="text-[10px] text-muted-foreground">
              <T>Strana</T> {page} {tt('z')} {totalPages} · {tt('Celkovo')} {serverTotal}{' '}
              {tt('objednávok')} · <T>Stav</T> <span className="text-primary">🔒</span>{' '}
              <T>vyžaduje overenie zamestnanca</T>
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> <T>Predchádzajúca</T>
              </button>
              <span className="text-xs font-bold text-foreground">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={page >= totalPages}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
              >
                <T>Ďalšia</T> <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {pendingAction && (
        <EmployeeAuthModal
          meta={pendingAction}
          onClose={() => setPendingAction(null)}
          onSuccess={handleAuthSuccess}
          // Both admin overrides are ordinary PATCHes behind a password: the
          // modal checks the credentials and this page applies the change.
          verifyOnly={!!pendingAction.useDirectPatch || !!pendingAction.newPaymentMethod}
        />
      )}
    </div>
  )
}

function SummaryCard({
  label,
  value,
  tone,
  note,
}: {
  label: string
  value: string
  tone: string
  /** §9.147 — the rule behind the figure, under it rather than in a tooltip: whoever is confused by
    * the number is not hovering over it. */
  note?: string
}) {
  return (
    <div className="bg-card border border-border rounded-2xl px-4 py-3">
      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">
        {label}
      </p>
      <p className={cn('text-xl font-bold mt-0.5', tone)}>{value}</p>
      {note !== undefined && (
        <p className="text-[10px] text-muted-foreground mt-0.5" data-card-note>
          {note}
        </p>
      )}
    </div>
  )
}

/** The export tab shows the same rows without the controls that change them. */
function ExportRow({ order, channels }: { order: OrderListItem; channels: readonly FilterChannel[] }) {
  const TypeIcon = TYPE_ICONS[order.type ?? ''] ?? Store
  return (
    <tr className="border-b border-border hover:bg-muted/20 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-xs font-bold text-primary">#{order.orderNumber}</span>
          <SourceBadge source={order.source} />
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <TypeIcon className="w-3.5 h-3.5" />
          {channelName(order.type ?? '', channels)}
        </span>
      </td>
      <td className="px-4 py-3 font-medium text-foreground text-sm">{order.customerName || '-'}</td>
      <td className="px-4 py-3 text-[10px] text-muted-foreground truncate max-w-[160px]">
        {order.items?.map((line) => `${line.quantity ?? 0}× ${line.productName ?? ''}`).join(', ') ||
          '-'}
      </td>
      <td className="px-4 py-3 text-right font-bold text-foreground">{formatMoney(order.total)}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <StatusPill status={order.status} />
          <EditedFlag order={order} />
          {/* §9.286 - this tab's rows do not expand, so the badge beside it leads nowhere. The name
            * is already on the list payload; without it a touch user learns nothing at all here. */}
          {order.isEdited && order.updatedByName && (
            <span className="text-[10px] text-red-500 font-medium" data-edited-by>
              {order.updatedByName}
            </span>
          )}
        </div>
      </td>
    </tr>
  )
}
